import { useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import api, { API_BASE_URL } from './api';

const STORAGE_KEY = 'bg_support_conversation_id';

function formatTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Nicosia',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  } catch (_err) {
    return d.toLocaleTimeString();
  }
}

function SendIcon({ color = 'currentColor' } = {}) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3.4 20.2 21 12 3.4 3.8 3 10.3l10 1.7-10 1.7.4 6.5Z"
        fill={color}
      />
    </svg>
  );
}

export default function SupportWidget({
  token,
  bottomOffset = 16,
  dock = false,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [storedConversationId, setStoredConversationId] = useState(() => (localStorage.getItem(STORAGE_KEY) || ''));
  const [unread, setUnread] = useState(0);
  const socketRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!API_BASE_URL) return undefined;
    const socket = io(`${API_BASE_URL}/support`, {
      transports: ['websocket'],
      auth: { token: token || '' },
    });
    socketRef.current = socket;
    return () => {
      socketRef.current = null;
      socket.disconnect();
    };
  }, [token]);

  useEffect(() => {
    if (!storedConversationId) return undefined;
    let active = true;
    (async () => {
      try {
        const res = await api.get(`/support/conversations/${storedConversationId}/messages?limit=1`);
        const conv = res.data?.data?.conversation || null;
        if (active && conv && conv.unreadForUser !== undefined) {
          setUnread(Number(conv.unreadForUser || 0));
        }
      } catch (_err) {
        // ignore (e.g., guest IP mismatch)
      }
    })();
    return () => { active = false; };
  }, [storedConversationId]);

  const conversationId = useMemo(() => String(conversation?._id || ''), [conversation]);
  const effectiveConversationId = conversationId || storedConversationId;

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return undefined;
    if (!effectiveConversationId) return undefined;

    function join() {
      socket.emit('support:join', { conversationId: effectiveConversationId });
    }

    socket.on('connect', join);
    if (socket.connected) join();
    return () => socket.off('connect', join);
  }, [effectiveConversationId]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return undefined;

    function onMessage(msg) {
      const msgConvId = String(msg?.conversationId || '');
      if (!msgConvId || msgConvId !== String(effectiveConversationId)) return;
      setMessages((prev) => {
        const id = String(msg?._id || '');
        if (id && prev.some((m) => String(m._id) === id)) return prev;
        return [...prev, msg];
      });
    }

    socket.on('support:message', onMessage);
    return () => socket.off('support:message', onMessage);
  }, [effectiveConversationId]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return undefined;
    if (!effectiveConversationId) return undefined;

    function onConversationUpdate(payload) {
      const convId = String(payload?.conversationId || '');
      if (!convId || convId !== String(effectiveConversationId)) return;
      if (payload?.unreadForUser !== undefined && payload?.unreadForUser !== null) {
        setUnread(Number(payload.unreadForUser || 0));
      }
    }

    socket.on('support:conversation-update', onConversationUpdate);
    return () => socket.off('support:conversation-update', onConversationUpdate);
  }, [effectiveConversationId]);

  async function ensureConversation() {
    if (conversation?._id) return conversation;
    setLoading(true);
    setError('');
    try {
      const storedId = localStorage.getItem(STORAGE_KEY) || '';
      const res = await api.post('/support/conversations/start', {
        conversationId: storedId || undefined,
      });
      const conv = res.data?.data?.conversation || null;
      if (conv?._id) {
        localStorage.setItem(STORAGE_KEY, String(conv._id));
        setStoredConversationId(String(conv._id));
      }
      setConversation(conv);
      return conv;
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to start support chat');
      return null;
    } finally {
      setLoading(false);
    }
  }

  async function loadMessages(convId) {
    if (!convId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/support/conversations/${convId}/messages?limit=120`);
      const list = res.data?.data?.messages || [];
      setMessages(list);
      await api.post(`/support/conversations/${convId}/read`);
      setUnread(0);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }

  async function openChat() {
    setOpen(true);
    const conv = await ensureConversation();
    if (!conv?._id) return;
    const socket = socketRef.current;
    if (socket) {
      socket.emit('support:join', { conversationId: String(conv._id) });
    }
    await loadMessages(String(conv._id));
  }

  useEffect(() => {
    if (!open) return undefined;
    const id = setTimeout(() => {
      const el = bodyRef.current;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    }, 50);
    return () => clearTimeout(id);
  }, [open, messages.length, loading]);

  async function send() {
    if (!effectiveConversationId) return;
    const clean = String(text || '').trim();
    if (!clean) return;
    setSending(true);
    setError('');
    try {
      const res = await api.post(`/support/conversations/${effectiveConversationId}/messages`, {
        text: clean,
      });
      const msg = res.data?.data?.message;
      if (msg) {
        setMessages((prev) => (prev.some((m) => String(m._id) === String(msg._id)) ? prev : [...prev, msg]));
      }
      setText('');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  }

  const styles = {
    button: {
      position: dock ? 'relative' : 'fixed',
      left: 'auto',
      right: dock ? 'auto' : 16,
      bottom: dock ? 'auto' : bottomOffset,
      zIndex: 2500,
      borderRadius: 999,
      border: '1px solid rgba(215,177,106,0.9)',
      background: 'linear-gradient(145deg, rgba(214,132,93,0.96), rgba(182,103,67,0.96))',
      color: '#fffaf7',
      padding: '12px 14px',
      cursor: 'pointer',
      fontWeight: 900,
      boxShadow: '0 10px 26px rgba(0,0,0,0.45)',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    },
    badge: {
      minWidth: 22,
      height: 22,
      padding: '0 6px',
      borderRadius: 999,
      background: '#b44f28',
      color: '#fffaf7',
      display: 'grid',
      placeItems: 'center',
      fontWeight: 900,
      fontSize: 12,
      lineHeight: 1,
    },
    panel: {
      position: 'fixed',
      left: 'auto',
      right: 16,
      bottom: bottomOffset + 60,
      zIndex: 2500,
      width: 'min(420px, calc(100vw - 32px))',
      height: 'min(560px, calc(100vh - 110px))',
      borderRadius: 18,
      border: '1px solid rgba(215,177,106,0.22)',
      background: 'rgba(52, 28, 18, 0.96)',
      color: '#fffaf7',
      overflow: 'hidden',
      display: 'grid',
      gridTemplateRows: 'auto minmax(0, 1fr) auto',
      boxShadow: '0 18px 60px rgba(0,0,0,0.55)',
    },
    header: {
      padding: 12,
      borderBottom: 'none',
      display: 'flex',
      justifyContent: 'space-between',
      gap: 10,
      alignItems: 'center',
      fontWeight: 900,
    },
    close: {
      border: '1px solid rgba(215,177,106,0.24)',
      background: 'rgba(0,0,0,0.15)',
      color: '#fffaf7',
      borderRadius: 12,
      padding: '8px 10px',
      cursor: 'pointer',
      fontWeight: 900,
    },
    body: {
      padding: 12,
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
      overscrollBehavior: 'contain',
      touchAction: 'pan-y',
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    },
    bubble: (mine) => ({
      alignSelf: mine ? 'flex-end' : 'flex-start',
      maxWidth: '82%',
      borderRadius: 16,
      padding: '8px 10px',
      background: mine ? 'rgba(214,132,93,0.98)' : 'rgba(255,255,255,0.08)',
      border: '1px solid rgba(215,177,106,0.14)',
      color: '#fffaf7',
      display: 'inline-block',
      width: 'fit-content',
    }),
    msgText: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.35, fontSize: 16 },
    timeInline: { marginLeft: 8, fontSize: 11, opacity: 0.75, whiteSpace: 'nowrap' },
    composer: {
      padding: 12,
      borderTop: '1px solid rgba(215,177,106,0.18)',
      display: 'grid',
      gap: 10,
    },
    inputWrap: { position: 'relative' },
    input: {
      width: '100%',
      borderRadius: 14,
      border: '1px solid rgba(215,177,106,0.22)',
      padding: '10px 56px 10px 10px',
      outline: 'none',
      background: 'rgba(0,0,0,0.18)',
      color: '#fffaf7',
      resize: 'none',
      minHeight: 46,
      fontSize: 15,
      lineHeight: 1.3,
    },
    sendIconBtn: (disabled) => ({
      position: 'absolute',
      right: 8,
      top: 8,
      bottom: 8,
      width: 40,
      borderRadius: 14,
      border: '1px solid rgba(215,177,106,0.22)',
      background: disabled ? 'rgba(255,255,255,0.08)' : 'rgba(214,132,93,0.98)',
      color: '#fffaf7',
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'grid',
      placeItems: 'center',
    }),
    error: { color: '#ffb4b4', fontWeight: 900, fontSize: 12 },
    hint: { color: 'rgba(246,231,193,0.86)', fontSize: 12, fontWeight: 800 },
  };

  return (
    <>
      {!open ? (
        <button type="button" onClick={openChat} style={styles.button}>
          <span>Need help ?</span>
          {unread > 0 && <span style={styles.badge}>{unread > 99 ? '99+' : unread}</span>}
        </button>
      ) : (
        <>
          <div style={styles.panel}>
            <div style={styles.header}>
              <div>Need help ?</div>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setError('');
                }}
                style={styles.close}
              >
                Close
              </button>
            </div>

            <div ref={bodyRef} className="support-scroll" style={styles.body}>
              {error && <div style={styles.error}>{error}</div>}
              {loading && <div style={styles.hint}>Loading…</div>}
              {!loading && messages.length === 0 && <div style={styles.hint}>Send a message to start.</div>}
              {!loading && messages.map((m) => {
                const mine = m.senderType === 'user';
                return (
                  <div key={m._id || `${m.createdAt}-${Math.random()}`} style={styles.bubble(mine)}>
                    {m.text && (
                      <div style={styles.msgText}>
                        {m.text}
                        <span style={styles.timeInline}>{formatTime(m.createdAt)}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={styles.composer}>
              <div style={styles.inputWrap}>
                <textarea
                  rows={2}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Type your message…"
                  style={styles.input}
                />
                <button type="button" onClick={send} disabled={sending} style={styles.sendIconBtn(sending)}>
                  <SendIcon />
                </button>
              </div>
              <div style={{ height: 0 }} />
            </div>
          </div>
        </>
      )}
    </>
  );
}
